import React from 'react';
import BillToLookupField from '../BillToLookupField';
import CarrierLookupField from '../CarrierLookupField';
import PortLookupField from '../PortLookupField';
import { LocationLookupField } from './LocationLookupField';

/**
 * Async FK pickers available to a `type: 'lookup'` field, keyed by `lookupTarget`.
 *
 * These targets are too large to preload into a select — carriers alone is ~23,700
 * active rows — so they search the server per keystroke and render a result card per
 * hit, which `optionsEndpoint` cannot express. Rather than invent a generic search
 * descriptor, each entry adapts an EXISTING, already-tested wrapper
 * (BillToLookupField and friends, all built on <LookupSearchField>) to one contract:
 * a {recno, name} pair in, the same out.
 *
 * The adapters exist because those wrappers differ slightly — CarrierLookupField
 * takes no `label` and carries a third `scac` field, LocationMapField takes no
 * `required` — and the drawer shouldn't have to know that.
 *
 * Add a target here rather than reaching for a bespoke drawer. The dropdown portals
 * into the enclosing <dialog>, which the generic drawer is, so it isn't clipped.
 */
export interface LookupValue {
  /** The stored FK. 0 = unset, which the field spec's fkZeroBlank writes back as '0'. */
  recno: number;
  /** Display text. Free-typed text sets recno 0 and keeps the text, which is how the
   *  wrappers signal "unlinked" — the drawer persists the FK, not this. */
  name: string;
}

export interface LookupFieldProps {
  value: LookupValue;
  onChange: (v: LookupValue) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  /** Orange-highlight the input: the stored FK's target is soft-deleted or missing.
   *  These pickers search ACTIVE records per keystroke, so a dead reference still
   *  resolves to a name — without this it looks exactly like a healthy value. */
  invalid?: boolean;
}

export const LOOKUP_FIELDS: Record<string, React.FC<LookupFieldProps>> = {
  // cms.billtos — search by name or recno, card shows address + Sage tag / terms / AR.
  billto: ({ value, onChange, label, required, disabled, invalid }) => (
    <BillToLookupField
      label={label}
      required={required}
      disabled={disabled}
      invalid={invalid}
      value={value}
      onChange={(v) => onChange({ recno: v.recno, name: v.name })}
    />
  ),
  // cms.locations — card shows address / city / state.
  location: ({ value, onChange, label, required, disabled, invalid }) => (
    <LocationLookupField
      label={label}
      required={required}
      disabled={disabled}
      invalid={invalid}
      value={value}
      onChange={(v) => onChange({ recno: v.recno, name: v.name })}
    />
  ),
  // cms.ports — search by name, UN/LOCODE or recno.
  port: ({ value, onChange, label, required, disabled, invalid }) => (
    <PortLookupField
      label={label}
      required={required}
      disabled={disabled}
      invalid={invalid}
      value={value}
      onChange={(v) => onChange({ recno: v.recno, name: v.name })}
    />
  ),
  // highway.carrierprofile — ~23,700 active, so search-only. The wrapper renders its
  // own label and tracks the carrier's SCAC; we keep only the {recno, name} the
  // drawer persists, and label it ourselves for consistency with the other fields.
  carrier: ({ value, onChange, label, required, disabled, invalid }) => (
    <div>
      <label className="block text-sm font-medium text-label mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <CarrierLookupField
        disabled={disabled}
        invalid={invalid}
        value={{ recno: value.recno, name: value.name, scac: '' }}
        onChange={(v) => onChange({ recno: v.recno, name: v.name })}
      />
    </div>
  ),
};

export default LOOKUP_FIELDS;
